/**
 * The Odoo shell of the branch page as state.
 *
 * « Ouvrir un Odoo shell » reads the tree first — the list on screen may be
 * stale — and opens the terminal on the shell pod when one is ready;
 * otherwise it starts the shell (`odooShell.ts`), reads the tree again every
 * {@link SHELL_POLL_INTERVAL_MS} until its pod is ready, and opens the
 * terminal on it — or gives up after {@link SHELL_START_TIMEOUT_MS}. That
 * flow never sends a stop.
 *
 * « Arrêter » sends ONE stop (a second press while it runs does nothing),
 * closes the terminals open on the pod, then reads the tree every
 * {@link SHELL_POLL_INTERVAL_MS} until no shell pod is listed any more — or
 * gives up after {@link SHELL_STOP_TIMEOUT_MS}. ArgoCD answers the stop as
 * soon as the Deployment is scaled to 0, while the pod is still there,
 * terminating: reading the tree once right after would still list it.
 *
 * Both waits pause while the tab is hidden and read at once when it is shown
 * again (`useVisiblePolling`); their timeouts are wall-clock time.
 *
 * Failures are posted with the alert API, as the page's other actions.
 * Tied to one environment (`envKey`): moving to another branch does not
 * unmount the page, and a wait started on dev-a must neither open a terminal
 * on dev-b nor leave its spinner there.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import {
  autheliaAuthApiRef,
  type PodInfo,
  projectEnvironmentsTranslationRef,
  useVisiblePolling,
} from '@internal/plugin-waadoo-core';
import { isShellPod } from './odooPods';
import {
  readyShellPod,
  requestShellAction,
  SHELL_POLL_INTERVAL_MS,
  SHELL_START_TIMEOUT_MS,
  SHELL_STOP_TIMEOUT_MS,
  ShellActionError,
  type ShellAction,
} from './odooShell';
import { odooTranslationRef } from './translation';

export type ShellPhase = 'idle' | 'starting' | 'stopping';

/** A wait for the tree to say the shell is ready, or gone. */
interface Wait {
  /** The run it belongs to: a newer one cancels it. */
  mine: number;
  /** Epoch milliseconds past which it gives up. */
  deadline: number;
  done: (pods: PodInfo[]) => boolean;
  onTimeout: () => void;
}

export interface UseOdooShellOptions {
  /** `<project>/<branch>`: what a wait in progress belongs to. */
  envKey: string;
  project: string;
  namespace: string | undefined;
  appName: string | undefined;
  /** The pods the page shows now. */
  pods: PodInfo[];
  /**
   * Reads the tree again, updates the page's pods and resolves them —
   * undefined when the answer is no longer the page's (another load).
   */
  reloadPods: () => Promise<PodInfo[] | undefined>;
  /** Opens the embedded terminal on a pod. */
  openTerminal: (pod: string) => void;
  /** Closes every embedded terminal open on a pod. */
  closeTerminals: (pod: string) => void;
}

export interface OdooShell {
  phase: ShellPhase;
  /** The pod being stopped, while `phase` is `stopping`. */
  stoppingPod?: string;
  open: () => void;
  stop: (pod: string) => void;
}

export function useOdooShell(options: UseOdooShellOptions): OdooShell {
  // Everything but `envKey` is read through `latest`, at the time of use.
  const { envKey } = options;
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const autheliaAuth = useApi(autheliaAuthApiRef);
  const alertApi = useApi(alertApiRef);
  const { t } = useTranslationRef(odooTranslationRef);

  const [state, setState] = useState<{
    phase: ShellPhase;
    stoppingPod?: string;
  }>({ phase: 'idle' });
  // The phase as of now, for the guards: the state is only seen by the next
  // render, and a second press may come before it.
  const busy = useRef(false);
  // The latest of what changes on every render, read by the async steps
  // below: a wait outlives the render that started it.
  const latest = useRef(options);
  latest.current = options;
  const tRef = useRef(t);
  tRef.current = t;
  // Le « pas de session Authelia » de la page, commun à ses autres actions.
  const { t: tPage } = useTranslationRef(projectEnvironmentsTranslationRef);
  const tPageRef = useRef(tPage);
  tPageRef.current = tPage;
  // Bumped to cancel what runs: a change of environment, an unmount.
  const run = useRef(0);
  // The wait in progress — what the poll below reads the tree for — and
  // whether there is one, which turns the poll on.
  const wait = useRef<Wait>();
  const [waiting, setWaiting] = useState(false);

  const endWait = useCallback(() => {
    wait.current = undefined;
    setWaiting(false);
  }, []);

  useEffect(() => {
    busy.current = false;
    setState({ phase: 'idle' });
    return () => {
      run.current += 1;
      busy.current = false;
      endWait();
    };
  }, [envKey, endWait]);

  const settle = useCallback(
    (mine: number, message?: string) => {
      if (run.current !== mine) {
        return;
      }
      busy.current = false;
      setState({ phase: 'idle' });
      if (message) {
        alertApi.post({ message, severity: 'error', display: 'transient' });
      }
    },
    [alertApi],
  );

  const failureMessage = useCallback((error: unknown, action: ShellAction) => {
    const failure =
      error instanceof ShellActionError ? error.failure : 'failed';
    const tr = tRef.current;
    switch (failure) {
      case 'forbidden':
        return tr('shell.error.forbidden');
      case 'missing':
        return tr('shell.error.missing');
      case 'noIdentity':
        return tPageRef.current('error.noIdentity');
      default:
        return action === 'start'
          ? tr('shell.error.startFailed')
          : tr('shell.error.stopFailed');
    }
  }, []);

  const request = useCallback(
    (action: ShellAction) => {
      const current = latest.current;
      if (!current.namespace || !current.appName) {
        return Promise.reject(new ShellActionError('failed', 'No target'));
      }
      return requestShellAction({
        fetchApi,
        discoveryApi,
        autheliaAuth,
        project: current.project,
        namespace: current.namespace,
        appName: current.appName,
        action,
      });
    },
    [fetchApi, discoveryApi, autheliaAuth],
  );

  /** The tree again; undefined when it did not answer this time. */
  const reload = useCallback(async () => {
    try {
      return await latest.current.reloadPods();
    } catch {
      return undefined;
    }
  }, []);

  /**
   * Reads the tree every interval until `done` says so (true), or until
   * `timeoutMs` — then `onTimeout`. A tree that does not answer is asked
   * again at the next tick.
   */
  const poll = useCallback(
    (
      mine: number,
      timeoutMs: number,
      done: (pods: PodInfo[]) => boolean,
      onTimeout: () => void,
    ) => {
      wait.current = {
        mine,
        deadline: Date.now() + timeoutMs,
        done,
        onTimeout,
      };
      setWaiting(true);
    },
    [],
  );

  // One tick of the wait in progress.
  const tick = useCallback(async () => {
    const current = wait.current;
    if (!current) {
      return;
    }
    const pods = await reload();
    if (run.current !== current.mine || wait.current !== current) {
      return;
    }
    if (pods && current.done(pods)) {
      endWait();
      return;
    }
    if (Date.now() >= current.deadline) {
      endWait();
      current.onTimeout();
    }
  }, [reload, endWait]);

  useVisiblePolling(tick, SHELL_POLL_INTERVAL_MS, waiting);

  const open = useCallback(() => {
    if (busy.current) {
      return;
    }
    busy.current = true;
    const mine = ++run.current;
    endWait();
    setState({ phase: 'starting' });

    const go = async () => {
      // The list on screen may be stale: a shell started meanwhile, or
      // stopped on its own.
      const fresh = (await reload()) ?? latest.current.pods;
      if (run.current !== mine) {
        return;
      }
      const ready = readyShellPod(fresh);
      if (ready) {
        settle(mine);
        latest.current.openTerminal(ready.name);
        return;
      }
      try {
        await request('start');
      } catch (error: unknown) {
        settle(mine, failureMessage(error, 'start'));
        return;
      }
      if (run.current !== mine) {
        return;
      }
      poll(
        mine,
        SHELL_START_TIMEOUT_MS,
        pods => {
          const pod = readyShellPod(pods);
          if (!pod) {
            return false;
          }
          settle(mine);
          latest.current.openTerminal(pod.name);
          return true;
        },
        () => settle(mine, tRef.current('shell.error.timeout')),
      );
    };
    void go();
  }, [reload, request, poll, settle, failureMessage, endWait]);

  const stop = useCallback(
    (pod: string) => {
      if (busy.current) {
        return;
      }
      busy.current = true;
      const mine = ++run.current;
      endWait();
      setState({ phase: 'stopping', stoppingPod: pod });

      const go = async () => {
        try {
          await request('stop');
        } catch (error: unknown) {
          settle(mine, failureMessage(error, 'stop'));
          return;
        }
        if (run.current !== mine) {
          return;
        }
        // Its session would end with the pod anyway; closed here, the
        // panel goes now rather than on an error.
        latest.current.closeTerminals(pod);
        poll(
          mine,
          SHELL_STOP_TIMEOUT_MS,
          pods => {
            if (pods.some(item => isShellPod(item))) {
              return false;
            }
            settle(mine);
            return true;
          },
          () => settle(mine, tRef.current('shell.error.stopTimeout')),
        );
      };
      void go();
    },
    [request, poll, settle, failureMessage, endWait],
  );

  return { ...state, open, stop };
}
